import type { ReactNode } from "react";

/**
 * The Ice Blue stroke glyph set: one line weight, rounded joins, 24-unit
 * grid. Shared by the encounter context rail and the patient profile rail so
 * a section reads the same in both places (one record, two views). Add a
 * glyph here rather than inline so every surface stays on the same stroke.
 */
export function Glyph({
  size,
  width = 1.7,
  children,
}: {
  readonly size: number;
  readonly width?: number;
  readonly children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function IconFlask() {
  return (
    <Glyph size={17}>
      <path d="M10 3v5L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L14 8V3" />
      <path d="M8.5 3h7M7 15h10" />
    </Glyph>
  );
}

export function IconPill() {
  return (
    <Glyph size={17}>
      <rect x="3.5" y="8.8" width="17" height="6.4" rx="3.2" transform="rotate(-40 12 12)" />
      <path d="M9.2 9.2l5.6 5.6" />
    </Glyph>
  );
}

export function IconHistory() {
  return (
    <Glyph size={17}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </Glyph>
  );
}

export function IconFolder() {
  return (
    <Glyph size={17}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </Glyph>
  );
}

export function IconMail() {
  return (
    <Glyph size={17}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </Glyph>
  );
}

export function IconClose() {
  return (
    <Glyph size={14} width={2}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Glyph>
  );
}

export function IconPerson() {
  return (
    <Glyph size={17}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </Glyph>
  );
}

/** Past medical history: a clipboard with three entry lines. */
export function IconClipboard() {
  return (
    <Glyph size={17}>
      <rect x="5" y="4.5" width="14" height="16.5" rx="2" />
      <path d="M9 4.5V3.5h6v1M8.5 10h7M8.5 13.5h7M8.5 17h4.5" />
    </Glyph>
  );
}

/** Family history: two figures, one behind the other. */
export function IconFamily() {
  return (
    <Glyph size={17}>
      <circle cx="9" cy="8.5" r="3.2" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M15.5 5.6a3 3 0 0 1 0 5.8M17 14.2a5.6 5.6 0 0 1 4 5.8" />
    </Glyph>
  );
}

/** Social history: a house — where and how the patient lives. */
export function IconHome() {
  return (
    <Glyph size={17}>
      <path d="M3.5 11.5L12 4l8.5 7.5" />
      <path d="M6 10v10h12V10" />
      <path d="M10 20v-5.5h4V20" />
    </Glyph>
  );
}

/** Allergies: the warning triangle every chart reads at a glance. */
export function IconAlert() {
  return (
    <Glyph size={17}>
      <path d="M12 3.5L21.5 20h-19z" />
      <path d="M12 9.5v4.5M12 17.2v.3" />
    </Glyph>
  );
}

/** Immunizations: a syringe on the diagonal. */
export function IconSyringe() {
  return (
    <Glyph size={17}>
      <path d="M18 6l-1.5-1.5M20 8l-1.5-1.5M17.5 5.5l-2.5 2.5" />
      <rect x="6.6" y="8.4" width="10" height="5.6" rx="1.2" transform="rotate(45 11.6 11.2)" />
      <path d="M8 16l-4 4" />
    </Glyph>
  );
}

/** Imaging & tests: a viewer frame with a waveform. */
export function IconScan() {
  return (
    <Glyph size={17}>
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
      <path d="M6.5 12.5h3l1.5-3 2 6 1.5-3h3" />
    </Glyph>
  );
}

/** Consults: a stethoscope — the specialist's report back. */
export function IconStethoscope() {
  return (
    <Glyph size={17}>
      <path d="M6 4v5.5a4 4 0 0 0 8 0V4" />
      <path d="M10 13.5v2.5a4 4 0 0 0 8 0v-1.5" />
      <circle cx="18" cy="12" r="2" />
    </Glyph>
  );
}

/*
 * Filling in a received fax (EXECDEV-207): the editor's tools and the button that opens it.
 * Sized by the caller, so the toolbar and the page's own controls can differ.
 */

/** Fill in and fax back: a page with a pen across its corner. */
export function IconFillIn({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M13 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21H11" />
      <path d="M13 3l5 5v2.5M8.5 9h5M8.5 12.5h3" />
      <path d="M14 20.5l.6-2.6 4.6-4.6a1.4 1.4 0 0 1 2 2l-4.6 4.6z" />
    </Glyph>
  );
}

export function IconTickMark({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size} width={2}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </Glyph>
  );
}

export function IconCrossMark({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size} width={2}>
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </Glyph>
  );
}

export function IconTextMark({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M5.5 6.5V5h13v1.5M12 5v14M9.5 19h5" />
    </Glyph>
  );
}

export function IconDateMark({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <rect x="4.5" y="5.5" width="15" height="14" rx="1.5" />
      <path d="M4.5 10h15M8.5 3.5v4M15.5 3.5v4M8.5 14h2M13.5 14h2" />
    </Glyph>
  );
}

export function IconSignatureMark({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M3.5 15.5c2.2 0 3.2-8 5.6-8 2 0 .4 7.2 2.7 7.2 1.6 0 2-3.4 3.6-3.4 1.4 0 1.4 2.3 2.8 2.3 1 0 1.6-.8 2.3-1.6" />
      <path d="M3.5 19.5h17" />
    </Glyph>
  );
}

export function IconUndo({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M8.5 6.5L4.5 10.5l4 4" />
      <path d="M4.5 10.5H15a4.5 4.5 0 0 1 0 9h-3" />
    </Glyph>
  );
}

export function IconRedo({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M15.5 6.5l4 4-4 4" />
      <path d="M19.5 10.5H9a4.5 4.5 0 0 0 0 9h3" />
    </Glyph>
  );
}

export function IconMinus({ size = 14 }: { readonly size?: number }) {
  return (
    <Glyph size={size} width={2}>
      <path d="M6 12h12" />
    </Glyph>
  );
}

export function IconPlus({ size = 14 }: { readonly size?: number }) {
  return (
    <Glyph size={size} width={2}>
      <path d="M12 6v12M6 12h12" />
    </Glyph>
  );
}

/** An open book: the patient handout (EXECDEV-251), the same glyph as the education kind tile. */
export function IconHandout({ size = 16 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M4 5.5A2 2 0 0 1 6 4h5v15H6a2 2 0 0 0-2 2zM20 5.5A2 2 0 0 0 18 4h-5v15h5a2 2 0 0 1 2 2z" />
    </Glyph>
  );
}

export function IconTrash({ size = 14 }: { readonly size?: number }) {
  return (
    <Glyph size={size}>
      <path d="M4.5 7h15M10 7V4.5h4V7M6.5 7l1 12.5h9l1-12.5M10 11v5M14 11v5" />
    </Glyph>
  );
}
